import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, IconButton, CircularProgress,
    Autocomplete, MenuItem, Alert, Divider, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

import api from '../../services/api';
import BuscaPaciente from '../BuscaPaciente';
import { toastSuccess, alertValidacao } from '../../services/alerts';
import { formatarMoeda, formatarData, MODALIDADE_LABEL } from '../../services/convenios';

const SectionTitle = ({ children, descricao }) => (
    <Box sx={{ mb: descricao ? 2 : 3 }}>
        <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
            {children}
        </Typography>
        {descricao && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1.5 }}>
                {descricao}
            </Typography>
        )}
    </Box>
);

/** Só sessão realizada e com convênio vinculado gera guia. */
const elegivel = (s) => (s.statusSessao === 'REALIZADA' || s.statusSessao === 'REALIZADA_ALTERADA')
    && !!s.idPacientePlanoSaude;

export default function GuiaForm() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const idSessaoParam = searchParams.get('sessao');

    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [sessoes, setSessoes] = useState([]);
    const [sessaoSelecionada, setSessaoSelecionada] = useState(null);
    const [carregandoSessoes, setCarregandoSessoes] = useState(false);

    const [procedimentos, setProcedimentos] = useState([]);
    const [form, setForm] = useState({
        codigoProcedimento: '', quantidade: 1, numeroGuia: '', senhaAutorizacao: '', observacoes: '',
    });
    const [valorVigente, setValorVigente] = useState(null);
    const [avisoValor, setAvisoValor] = useState('');
    const [saving, setSaving] = useState(false);

    /* A lista de pacientes não é mais baixada no mount: o BuscaPaciente consulta
       o servidor conforme o usuário digita. */

    // Chamada com ?sessao=123 (vindo da tela de atendimentos): já abre apontada.
    useEffect(() => {
        if (!idSessaoParam) return;
        api.get(`/sessoes/${idSessaoParam}`)
            .then(r => {
                const s = r.data;
                setSessaoSelecionada({ ...s, id_sessao: s.id });
                setPacienteSelecionado({ id_paciente: s.idPaciente, nomeCompleto: s.nomePaciente });
            })
            .catch(() => {});
    }, [idSessaoParam]);

    const carregarSessoes = useCallback(async () => {
        if (!pacienteSelecionado) { setSessoes([]); return; }
        setCarregandoSessoes(true);
        try {
            const { data } = await api.get(`/sessoes/paciente/${pacienteSelecionado.id_paciente}`);
            const lista = (Array.isArray(data) ? data : []).filter(elegivel);
            setSessoes(lista.map(s => ({ ...s, id_sessao: s.id })));
        } catch {
            setSessoes([]);
        } finally {
            setCarregandoSessoes(false);
        }
    }, [pacienteSelecionado]);

    useEffect(() => { carregarSessoes(); }, [carregarSessoes]);

    // Tabela de valores do convênio da sessão escolhida.
    useEffect(() => {
        if (!sessaoSelecionada?.idPlanoSaude) { setProcedimentos([]); return; }
        api.get(`/planos-saude/${sessaoSelecionada.idPlanoSaude}/procedimentos`)
            .then(r => setProcedimentos(Array.isArray(r.data) ? r.data : []))
            .catch(() => setProcedimentos([]));
    }, [sessaoSelecionada?.idPlanoSaude]);

    /**
     * Prévia do valor: resolvido pelo back com a data do atendimento, igual à
     * emissão. Sem isso a tela mostraria o valor de hoje, que pode ser outro.
     */
    useEffect(() => {
        setValorVigente(null);
        setAvisoValor('');
        const codigo = form.codigoProcedimento;
        const idPlano = sessaoSelecionada?.idPlanoSaude;
        const data = sessaoSelecionada?.dataHora?.substring(0, 10);
        if (!codigo || !idPlano || !data) return;

        api.get(`/planos-saude/${idPlano}/procedimentos/vigente`, {
            params: { codigo, data, modalidade: sessaoSelecionada.modalidadeSessao || undefined },
            skipGlobalError: true,
        })
            .then(r => setValorVigente(r.data))
            .catch(() => setAvisoValor(
                `Nenhum valor vigente em ${formatarData(data)} para o código ${codigo} neste convênio. `
                + 'Ajuste a tabela de valores antes de emitir.'));
    }, [form.codigoProcedimento, sessaoSelecionada]);

    const set = (campo) => (e) => setForm(prev => ({ ...prev, [campo]: e.target.value }));

    const handleEmitir = async () => {
        if (!sessaoSelecionada) { alertValidacao('Selecione o atendimento da guia.'); return; }
        if (!form.codigoProcedimento) { alertValidacao('Selecione o procedimento.'); return; }
        if (!form.quantidade || Number(form.quantidade) <= 0) { alertValidacao('Quantidade inválida.'); return; }

        setSaving(true);
        try {
            const { data } = await api.post('/guias', {
                idSessao: sessaoSelecionada.id_sessao,
                codigoProcedimento: form.codigoProcedimento,
                quantidade: Number(form.quantidade),
                numeroGuia: form.numeroGuia || null,
                senhaAutorizacao: form.senhaAutorizacao || null,
                observacoes: form.observacoes || null,
            });
            toastSuccess(`Guia ${data.numeroGuia || `#${data.id_guia_atendimento}`} emitida`);
            navigate('/guias');
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setSaving(false);
        }
    };

    const totalPrevisto = valorVigente
        ? Number(valorVigente.valor) * Number(form.quantidade || 1)
        : null;

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/guias')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', flexGrow: 1 }}>
                    Emitir guia
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <SectionTitle descricao="Só atendimento já realizado e com convênio vinculado aparece na lista. Atendimento particular não gera guia.">
                    Atendimento
                </SectionTitle>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <BuscaPaciente
                        label="Paciente *"
                        size="medium"
                        value={pacienteSelecionado}
                        onChange={(v) => { setPacienteSelecionado(v); setSessaoSelecionada(null); }}
                    />

                    <Autocomplete
                        options={sessoes}
                        loading={carregandoSessoes}
                        getOptionLabel={(o) => o
                            ? `${formatarData(o.dataHora?.substring(0, 10))} • ${o.nomePlanoSaude || 'Convênio'} • ${MODALIDADE_LABEL[o.modalidadeSessao] || o.modalidadeSessao || ''}`
                            : ''}
                        isOptionEqualToValue={(o, v) => o.id_sessao === v.id_sessao}
                        value={sessaoSelecionada}
                        onChange={(_, v) => { setSessaoSelecionada(v); setForm(p => ({ ...p, codigoProcedimento: '' })); }}
                        noOptionsText={pacienteSelecionado
                            ? 'Nenhum atendimento realizado por convênio sem guia emitida.'
                            : 'Escolha o paciente primeiro.'}
                        renderInput={(params) => <TextField {...params} label="Atendimento *" />}
                    />

                    {sessaoSelecionada && (
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip size="small" label={`Convênio: ${sessaoSelecionada.nomePlanoSaude || '-'}`} />
                            <Chip size="small" label={`Atendimento: ${formatarData(sessaoSelecionada.dataHora?.substring(0, 10))}`} />
                            <Chip size="small" label={`Modalidade: ${MODALIDADE_LABEL[sessaoSelecionada.modalidadeSessao] || '-'}`} />
                        </Box>
                    )}

                    <Divider />

                    <SectionTitle descricao="O valor vem da tabela do convênio resolvida pela data do atendimento — não é digitado aqui.">
                        Procedimento
                    </SectionTitle>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField select sx={{ flex: 2, minWidth: 260 }} label="Procedimento *"
                            value={form.codigoProcedimento} onChange={set('codigoProcedimento')}
                            disabled={!sessaoSelecionada}
                            helperText={!sessaoSelecionada ? 'Escolha o atendimento primeiro.' : ''}>
                            {procedimentos.map(p => (
                                <MenuItem key={p.id_procedimento_convenio} value={p.codigoProcedimento}>
                                    {p.codigoProcedimento} — {p.descricao}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField sx={{ flex: 1, minWidth: 120 }} type="number" label="Quantidade"
                            value={form.quantidade} onChange={set('quantidade')} inputProps={{ min: 1 }} />
                    </Box>

                    {avisoValor && <Alert severity="warning">{avisoValor}</Alert>}

                    {valorVigente && (
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f1f8f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    {valorVigente.descricao}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Vigência desde {formatarData(valorVigente.vigenciaInicio)}
                                    {valorVigente.vigenciaFim ? ` até ${formatarData(valorVigente.vigenciaFim)}` : ' (aberta)'}
                                    {' • unitário '}{formatarMoeda(valorVigente.valor)}
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                {formatarMoeda(totalPrevisto)}
                            </Typography>
                        </Box>
                    )}

                    <Divider />

                    <SectionTitle descricao="Podem chegar depois da autorização; o número é exigido para faturar a guia no lote.">
                        Dados da operadora
                    </SectionTitle>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField sx={{ flex: 1, minWidth: 160 }} label="Número da guia"
                            value={form.numeroGuia} onChange={set('numeroGuia')} />
                        <TextField sx={{ flex: 1, minWidth: 160 }} label="Senha de autorização"
                            value={form.senhaAutorizacao} onChange={set('senhaAutorizacao')} />
                    </Box>
                    <TextField label="Observações" multiline rows={2} inputProps={{ maxLength: 255 }}
                        value={form.observacoes} onChange={set('observacoes')} />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button variant="text" onClick={() => navigate('/guias')}>Cancelar</Button>
                    <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleEmitir} disabled={saving}>
                        {saving ? 'Emitindo...' : 'Emitir guia'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
